/**
 * Forgot Password Route
 * Public route for password recovery
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ForgotPasswordPage } from '../pages/Auth/ForgotPasswordPage'
import { ResetPasswordPage } from '../pages/Auth/ResetPasswordPage'
import { requireGuest } from '../router/guards'

const getRecoveryParams = (location: Location) => {
  const searchParams = new URLSearchParams(location.search)
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''))
  const getParam = (key: string) => searchParams.get(key) ?? hashParams.get(key)

  return {
    type: getParam('type'),
    accessToken: getParam('access_token'),
    refreshToken: getParam('refresh_token'),
    code: getParam('code'),
    error: getParam('error'),
    errorDescription: getParam('error_description'),
  }
}

// Forgot password route component with navigation handlers
const ForgotPasswordRouteComponent = () => {
  const navigate = useNavigate()
  const [isRecovery, setIsRecovery] = useState(false)
  const [recoveryToken, setRecoveryToken] = useState('')
  const [recoveryError, setRecoveryError] = useState<string | null>(null)

  useEffect(() => {
    const currentWindow = globalThis.window
    if (!currentWindow) {
      return
    }

    const { type, accessToken, code, error, errorDescription } = getRecoveryParams(currentWindow.location)

    if (error) {
      setRecoveryError(`Password reset failed: ${errorDescription || error}`)
      setIsRecovery(false)
      return
    }

    const token = accessToken || code || ''
    if (type === 'recovery' || token) {
      setIsRecovery(true)
      setRecoveryToken(token)

      if (currentWindow.location.hash) {
        currentWindow.history.replaceState(
          null,
          document.title,
          `${currentWindow.location.pathname}${currentWindow.location.search}`
        )
      }
    }
  }, [])

  if (isRecovery) {
    return <ResetPasswordPage token={recoveryToken} onBackToLogin={() => navigate({ to: '/login' })} />
  }

  return (
    <>
      {recoveryError && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '12px 20px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            fontSize: '14px',
          }}>
          {recoveryError}
        </div>
      )}
      <ForgotPasswordPage onBackToLogin={() => navigate({ to: '/login' })} />
    </>
  )
}

// Create forgot password route with guest guard
export const Route = createFileRoute('/forgot-password')({
  beforeLoad: requireGuest,
  component: ForgotPasswordRouteComponent,
})
